import { ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { toNewsAssetUrlForRender } from "@/lib/utils/apex-news";
import { formatNewsUtcDate } from "@/lib/utils/date";
import type { ThreadPost } from "@/lib/api/generated/model";

type ThreadArticleProps = {
  posts: ThreadPost[];
};

function resolveMediaItems(media: unknown) {
  const record = media && typeof media === "object" ? (media as Record<string, unknown>) : {};
  const items = Array.isArray(record.items) ? record.items : [];
  return items
    .map((item) => (item && typeof item === "object" ? (item as Record<string, any>) : null))
    .filter((item): item is Record<string, any> => Boolean(item))
    .map((item) => ({
      type: typeof item.type === "string" ? item.type : "unknown",
      url: typeof item.url === "string" ? item.url : "",
      previewUrl: typeof item.previewUrl === "string" ? item.previewUrl : undefined,
      width: typeof item.width === "number" ? item.width : undefined,
      height: typeof item.height === "number" ? item.height : undefined,
    }))
    .filter((item) => item.url.length > 0);
}

export function ThreadArticle({ posts }: ThreadArticleProps) {
  return (
    <section className="space-y-4">
      {posts.map((post, index) => {
        const mediaItems = resolveMediaItems(post.media || {});
        return (
          <Card key={post.id} className="border-border/80">
            <CardContent className="space-y-3 p-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">
                  {post.authorName} (@{post.authorHandle})
                </span>
                <span>#{index + 1}</span>
                {post.isRoot ? (
                  <span className="rounded bg-red-600 px-2 py-0.5 text-white">Root</span>
                ) : null}
                <span>{formatNewsUtcDate(post.postedAt, { includeTime: true })}</span>
              </div>

              <p className="whitespace-pre-wrap text-sm leading-relaxed">
                {post.translatedText || post.sourceText}
              </p>

              {mediaItems.length > 0 ? (
                <div className="grid gap-2 md:grid-cols-2">
                  {mediaItems.map((item, mediaIndex) => {
                    const mediaSrc =
                      toNewsAssetUrlForRender(item.url, {
                        referer: post.permalink,
                      }) || item.url;
                    const preview =
                      (item.previewUrl &&
                        (toNewsAssetUrlForRender(item.previewUrl, {
                          referer: post.permalink,
                        }) ||
                          item.previewUrl)) ||
                      undefined;
                    const key = `${post.id}:${mediaIndex}`;
                    if (item.type === "video" || item.type === "animated_gif") {
                      return (
                        <video
                          key={key}
                          src={mediaSrc}
                          poster={preview}
                          controls
                          preload="metadata"
                          className="h-full w-full rounded-lg border object-cover"
                        />
                      );
                    }
                    return (
                      <img
                        key={key}
                        src={mediaSrc}
                        alt={`post-media-${index + 1}-${mediaIndex + 1}`}
                        className="h-full w-full rounded-lg border object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    );
                  })}
                </div>
              ) : null}

              <details className="rounded-md border border-dashed p-3 text-sm">
                <summary className="cursor-pointer font-medium">查看原文</summary>
                <p className="mt-2 whitespace-pre-wrap text-muted-foreground">{post.sourceText}</p>
              </details>

              <a
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-500"
              >
                查看原帖
                <ExternalLink className="size-4" />
              </a>
            </CardContent>
          </Card>
        );
      })}
    </section>
  );
}
